import { makeAutoObservable } from 'mobx'
import { type RootStore } from './RootStore'
import { ComponentPropsWithoutRef, FunctionComponent } from 'react'

export type DrawerKey = 'backgrounds' | 'templates' | 'elements' | 'text'

/**
 * Tag a drawer component with a stable id used by `useBlankFrameHints` /
 * `useBlankDesignHints` to recognize tab-like drawers. The store copies the
 * id off the component at push time so it survives MobX's deep-observation
 * wrapping of function-valued properties.
 */
export const tabDrawer = <T extends object>(component: T, drawerKey: DrawerKey): T & { drawerKey: DrawerKey } =>
  Object.assign(component, { drawerKey })

export interface Options {
  newStack?: boolean
}
interface StackDrawer<C extends FunctionComponent<React.PropsWithChildren<any>>> {
  component: C
  props?: ComponentPropsWithoutRef<C>
  drawerKey?: DrawerKey
}

export class DrawersStore {
  rootStore: RootStore
  drawers: StackDrawer<FunctionComponent<React.PropsWithChildren<any>>>[] = []
  stackIndices: number[] = []
  snapResetObserver?: boolean

  constructor(rootStore: RootStore) {
    makeAutoObservable(this)
    this.rootStore = rootStore
    this.snapResetObserver = undefined
  }

  openDrawer = <C extends FunctionComponent<React.PropsWithChildren<any>>>(
    component: C,
    props?: ComponentPropsWithoutRef<C>,
    options?: Options,
  ) => {
    if (options?.newStack) {
      this.stackIndices.push(this.drawers.length)
    }

    // Extract drawerKey to a primitive at push time. MobX's deep observation
    // wraps function-valued properties stored in observable objects, stripping
    // off attached fields like `.drawerKey` or `.displayName` on the wrapper.
    // Reading the key here keeps it as plain data on the entry.
    const drawerKey = (component as { drawerKey?: DrawerKey }).drawerKey
    this.drawers.push({
      component,
      props,
      drawerKey,
    })
  }

  closeDrawer = () => {
    this.drawers = [...this.drawers].slice(0, this.drawers.length - 1)
    if (this.stackIndices.length > 0 && this.stackIndices[this.stackIndices.length - 1] === this.drawers.length) {
      this.stackIndices.pop()
    }
    this.snapResetObserver = undefined
  }

  closeAllDrawers = () => {
    this.stackIndices = []
    this.drawers = []
    this.snapResetObserver = undefined
  }

  closeStack = () => {
    const index = this.stackIndices.pop()
    if (index === undefined) {
      return
    }

    this.drawers = this.drawers.slice(0, index)
  }

  resetSnap = () => {
    this.snapResetObserver = !this.snapResetObserver
  }
}
